import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import type JawCorrection from './JawCorrection';
import type ToothCorrection from './ToothCorrection';
import { getMesialDistalNeighbors } from './toothContactUtils';

const CONTACT_COLOR = 0x4f9dff;
// World-space width (mm) of the contact line — a camera-facing ribbon, so it stays
// the same thickness regardless of zoom.
const CONTACT_LINE_WIDTH = 0.28;

/**
 * After the user finishes a translate/rotate, draws the PRECISE intersection
 * contour between the active tooth and its mesial/distal neighbours — the exact
 * triangle-triangle crossing curve from the BVH (`intersectMeshSegments`), not a
 * fuzzy proximity patch. The line is hosted on the neighbour; the active tooth is
 * not highlighted. If the teeth don't actually interpenetrate, nothing is drawn.
 */
export default class ToothContactOverlay {
  private readonly jaw: JawCorrection;
  private readonly overlays = new Map<ToothCorrection, LineSegments2>();

  constructor(jaw: JawCorrection) {
    this.jaw = jaw;
  }

  /** Recompute contact contours (call only on transform end, not during drag). */
  public update(active: ToothCorrection): void {
    this.clearOverlays();

    const neighbors = getMesialDistalNeighbors(active, this.jaw.teeth, this.jaw.getJawType());
    const neighborList = [neighbors.mesial, neighbors.distal].filter(Boolean) as ToothCorrection[];

    for (const neighbor of neighborList) {
      const line = this.buildIntersectionContour(neighbor, active);
      if (line) this.overlays.set(neighbor, line);
    }
  }

  public clear(): void {
    this.clearOverlays();
  }

  private clearOverlays(): void {
    for (const line of this.overlays.values()) {
      this.disposeOverlay(line);
    }
    this.overlays.clear();
  }

  /** Builds the exact intersection line of `other` against `host`, in `host` local space. */
  private buildIntersectionContour(host: ToothCorrection, other: ToothCorrection): LineSegments2 | null {
    let segments: Array<{ a: { x: number; y: number; z: number }; b: { x: number; y: number; z: number } }>;
    try {
      segments = host.intersectMeshSegments(other);
    } catch (e) {
      console.warn('ToothContactOverlay: intersectMeshSegments failed (missing BVH?)', e);
      return null;
    }
    if (segments.length === 0) return null;

    const positions = new Float32Array(segments.length * 6);
    segments.forEach((s, i) => {
      positions[i * 6 + 0] = s.a.x;
      positions[i * 6 + 1] = s.a.y;
      positions[i * 6 + 2] = s.a.z;
      positions[i * 6 + 3] = s.b.x;
      positions[i * 6 + 4] = s.b.y;
      positions[i * 6 + 5] = s.b.z;
    });

    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);

    const material = new LineMaterial({
      color: CONTACT_COLOR,
      linewidth: CONTACT_LINE_WIDTH,
      worldUnits: true,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      // Fat lines are real triangle geometry, so polygonOffset pulls the ribbon
      // toward the camera and avoids z-fighting along the shared seam.
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
    });

    const line = new LineSegments2(geometry, material);
    line.name = `contact-intersection-${host.getFDI()}`;
    line.renderOrder = 30;
    host.add(line);
    return line;
  }

  private disposeOverlay(line: LineSegments2): void {
    line.parent?.remove(line);
    line.geometry.dispose();
    line.material.dispose();
  }
}
